import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { generatePath, useLocation, useNavigate, useParams } from 'react-router-dom';

import { useDeepCompareMemo, captureError } from '@clatter/platform';
import { routesNames } from '../routes';
import { FolderManagerMimeTypes } from '../interfaces';
import {
  useGetFolderAssetsQuery,
  useGetSubFoldersQuery,
  useGetFolderPathQuery,
  useUpdateFolderMutation,
  useGetFolderPresentationsQuery,
} from '../services';

//region INTERFACES
interface useFolderManagerProps {
  fileInputRef?: React.RefObject<HTMLInputElement>;
  rootFolderId: number | null;
  selectedFolderId?: number | null;
  uploadUrl?: string;
  inMemory?: boolean;
  onlyFolders?: boolean;
  selectedThemeId?: number | null;
  clipBreadcrumbstoRoot?: boolean;
}

//endregion

export const useFolderManager = ({
  rootFolderId,
  selectedFolderId: selectedFromParent,
  inMemory = false,
  onlyFolders = false,
  clipBreadcrumbstoRoot = false,
}: useFolderManagerProps) => {
  const navigate = useNavigate();
  const { slug, folderId: urlFolderId } = useParams();
  const location = useLocation();

  const [selectedFolderId, setSelectedFolderId] = useState<number | null>(
    inMemory ? selectedFromParent || rootFolderId : null,
  );

  const folderId = useMemo(() => selectedFolderId || rootFolderId, [selectedFolderId, rootFolderId]);

  const { data: folderBreadcrumbs, isLoading: pathIsLoading } = useGetFolderPathQuery(
    { folderId: +folderId! },
    { skip: !folderId || isNaN(folderId) },
  );

  // Stable reference key: URL folderId when available, otherwise internal folderId
  const folderKey = urlFolderId ?? String(folderId);

  const lastFolderAssetsRef = useRef<{ folderKey: string; data: FolderManagerItem[] } | null>(null);

  const { currentData: rawAssetsData, isLoading: assetsDataLoading } = useGetFolderAssetsQuery(
    { parentId: Number(folderId) },
    { skip: !folderId || isNaN(folderId) || location.pathname === '/trash' || onlyFolders },
  );

  useEffect(() => {
    if (rawAssetsData !== undefined) {
      lastFolderAssetsRef.current = { folderKey, data: rawAssetsData };
    }
  }, [rawAssetsData, folderKey]);

  // When theme changes (same folder): rawAssetsData is undefined but ref matches → keep stale data visible
  // When folder changes: ref folderKey differs → show nothing until new data arrives
  const assetsData =
    rawAssetsData ??
    (lastFolderAssetsRef.current?.folderKey === folderKey ? lastFolderAssetsRef.current.data : undefined);

  const { currentData: subFoldersData, isLoading: subFoldersDataLoading } = useGetSubFoldersQuery(
    {
      parentId: Number(folderId),
    },
    { skip: !folderId || isNaN(folderId) || location.pathname === '/trash' },
  );

  const { currentData: presentationsData, isLoading: presentationsDataLoading } = useGetFolderPresentationsQuery(
    { folderId: Number(folderId) },
    { skip: !folderId || isNaN(folderId) || location.pathname === '/trash' || onlyFolders },
  );

  const [updateFolder] = useUpdateFolderMutation();

  const isLoading = assetsDataLoading || subFoldersDataLoading || pathIsLoading || presentationsDataLoading;

  const hierarchyData = useDeepCompareMemo(() => {
    if (onlyFolders) {
      return subFoldersData || [];
    }

    return [...(subFoldersData || []), ...(presentationsData || []), ...(assetsData || [])];
  }, [subFoldersData, assetsData, presentationsData, onlyFolders]);

  const handleRestoreFolders = async ({ itemsToRestore }: { itemsToRestore: FolderManagerItem[] }) => {
    for (const item of itemsToRestore) {
      if (item.type === FolderManagerMimeTypes.FOLDER) {
        await updateFolder({ folderId: item.id, data: { restore: true } });
      }
    }
  };

  const handleWorkspaceClick = useCallback(({ item }: { item: FolderManagerItem }) => {
    navigate(generatePath(routesNames.editProject, { projectId: item.id.toString() }));
  }, []);

  const handleFolderClick = ({ item }: { item: FolderManagerItem }) => {
    if (inMemory) {
      setSelectedFolderId(item.id);
      return;
    }

    if ('id' in item && slug) {
      navigate(routesNames.folders(slug, item.id.toString()));
    } else {
      captureError(new Error('Could not handle folder click! No data provided'), {
        component: 'useFolderManager',
        skipConsole: true,
      });
    }
  };
  //endregion

  useEffect(() => {
    if (inMemory && selectedFromParent && !selectedFolderId) {
      setSelectedFolderId(selectedFromParent);
    }
  }, [selectedFromParent, inMemory]);

  const breadcrumbs = useMemo(() => {
    const crumbs = folderBreadcrumbs || [];

    if (!clipBreadcrumbstoRoot || !rootFolderId) {
      return crumbs;
    }

    const rootIndex = crumbs.findIndex((b) => b.id === rootFolderId);

    return rootIndex !== -1 ? crumbs.slice(rootIndex) : crumbs;
  }, [folderBreadcrumbs, clipBreadcrumbstoRoot, rootFolderId]);

  return {
    handleFolderClick: handleFolderClick,
    handleRestoreFolders: handleRestoreFolders,
    handleWorkspaceClick: handleWorkspaceClick,
    isLoading: isLoading,
    assetsData: assetsData,
    breadcrumbs: breadcrumbs,
    hierarchyData: hierarchyData,
    selectedFolderId: rootFolderId,
  };
};
